import React, { useState, useMemo, useCallback } from 'react';
import { useLocale } from '@platform/core';
import { MagicGridProps, MagicGridRow, ShowField } from './types';
import { useMagicGridData } from './useMagicGridData';
import MagicGridToolbar from './MagicGridToolbar';
import MagicGridTable from './MagicGridTable';

export default function MagicGrid({
  firestorePath,
  items,
  editable = false,
  addable = false,
  deletable = false,
  duplicatable = false,
  importable = false,
  exportable = false,
  options,
  title,
}: MagicGridProps) {
  const { t } = useLocale();
  const { rows, loading, addRow, updateRow, deleteRow, addRows } =
    useMagicGridData(firestorePath, items);

  const [search, setSearch] = useState('');
  const [settingsMode, setSettingsMode] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [newRowData, setNewRowData] = useState<Record<string, any>>({});

  // Derive columns from data
  const allColumns = useMemo(() => {
    const colSet = new Set<string>();
    rows.forEach((row) =>
      Object.keys(row).forEach((k) => {
        if (k !== 'id') colSet.add(k);
      })
    );
    return Array.from(colSet);
  }, [rows]);

  // Show/hide field state
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, boolean>>({});

  const showFields: ShowField[] = useMemo(() => {
    const hideCols = new Set(options?.hideCols || []);
    return allColumns.map((key) => ({
      key,
      show: fieldOverrides[key] ?? !hideCols.has(key),
    }));
  }, [allColumns, fieldOverrides, options?.hideCols]);

  const handleShowFieldToggle = useCallback((key: string) => {
    setFieldOverrides((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  }, []);

  // Add row form
  const handleStartAdd = () => {
    const template: Record<string, any> = {};
    allColumns.forEach((col) => {
      // Determine default type from first row
      const sample = rows[0]?.[col];
      if (typeof sample === 'boolean') template[col] = false;
      else if (typeof sample === 'number') template[col] = 0;
      else template[col] = '';
    });
    setNewRowData(template);
    setAddMode(true);
  };

  const handleAddSubmit = async () => {
    await addRow(newRowData);
    setAddMode(false);
    setNewRowData({});
  };

  const handleAddCancel = () => {
    setAddMode(false);
    setNewRowData({});
  };

  // Duplicate
  const handleDuplicate = async (row: MagicGridRow) => {
    const { id: _id, ...data } = row;
    await addRow(data);
  };

  // CSV Export
  const handleExport = () => {
    if (rows.length === 0) return;
    const cols = allColumns;
    const header = cols.join(',');
    const csvRows = rows.map((row) =>
      cols
        .map((col) => {
          const val = row[col];
          if (val == null) return '';
          const str = String(val);
          return str.includes(',') || str.includes('"')
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        })
        .join(',')
    );
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // CSV Import
  const handleImport = async (importedRows: Record<string, any>[]) => {
    await addRows(importedRows);
  };

  if (loading) {
    return (
      <div className="mg-loading">
        <div className="loading-spinner" />
        <p>{t('general.loading') || 'Loading...'}</p>
      </div>
    );
  }

  const addFormHideCols = new Set([...(options?.hideCols || []), 'id']);
  const visibleAddCols = allColumns.filter((c) => !addFormHideCols.has(c));

  return (
    <div className="magic-grid">
      {title && <h3 className="mg-title">{title}</h3>}

      <MagicGridToolbar
        search={search}
        onSearchChange={setSearch}
        addable={addable}
        onAdd={handleStartAdd}
        importable={importable}
        onImport={handleImport}
        exportable={exportable}
        onExport={handleExport}
        settingsMode={settingsMode}
        onToggleSettings={() => setSettingsMode((s) => !s)}
      />

      {/* Inline Add Form */}
      {addMode && (
        <div className="mg-add-form">
          <div className="mg-add-fields">
            {visibleAddCols.map((col) => (
              <div key={col} className="mg-add-field">
                <label>{col}</label>
                {typeof newRowData[col] === 'boolean' ? (
                  <input
                    type="checkbox"
                    checked={newRowData[col]}
                    onChange={(e) =>
                      setNewRowData((d) => ({ ...d, [col]: e.target.checked }))
                    }
                  />
                ) : (
                  <input
                    className="mg-cell-input"
                    value={newRowData[col] ?? ''}
                    onChange={(e) =>
                      setNewRowData((d) => ({ ...d, [col]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddSubmit();
                      if (e.key === 'Escape') handleAddCancel();
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mg-add-actions">
            <button className="mg-btn mg-btn-primary" onClick={handleAddSubmit}>
              {t('common.add') || 'ADD'}
            </button>
            <button className="mg-btn mg-btn-outline" onClick={handleAddCancel}>
              {t('common.cancel') || 'CANCEL'}
            </button>
          </div>
        </div>
      )}

      <MagicGridTable
        rows={rows}
        search={search}
        options={options}
        editable={editable}
        deletable={deletable}
        duplicatable={duplicatable}
        showFields={showFields}
        onShowFieldToggle={handleShowFieldToggle}
        settingsMode={settingsMode}
        onUpdate={updateRow}
        onDelete={deleteRow}
        onDuplicate={handleDuplicate}
      />

      <style>{`
        .magic-grid {
          background: var(--surface-color, #fff);
          border-radius: var(--radius-lg, 12px);
          border: 1px solid var(--border-color, #e0e0e0);
          overflow: hidden;
        }

        .mg-title {
          padding: 1rem 1.5rem 0;
          margin: 0;
          font-size: 1.25rem;
        }

        .mg-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 3rem;
          gap: 1rem;
        }

        .mg-toolbar {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--border-color, #e0e0e0);
          flex-wrap: wrap;
        }

        .mg-toolbar-right {
          margin-inline-start: auto;
          display: flex;
          gap: 0.5rem;
        }

        .mg-search-wrap {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.375rem 0.75rem;
          border: 1px solid var(--border-color, #e0e0e0);
          border-radius: var(--radius-md, 8px);
          background: var(--background-color, #f5f5f5);
        }

        .mg-search-icon { font-size: 0.875rem; }

        .mg-search {
          border: none;
          background: transparent;
          outline: none;
          font-size: 0.875rem;
          min-width: 150px;
        }

        .mg-btn {
          padding: 0.375rem 0.75rem;
          border-radius: var(--radius-md, 8px);
          font-size: 0.8125rem;
          cursor: pointer;
          border: 1px solid var(--border-color, #e0e0e0);
          background: var(--surface-color, #fff);
          transition: all 0.15s;
          white-space: nowrap;
        }

        .mg-btn:hover { background: var(--background-color, #f5f5f5); }

        .mg-btn-primary {
          background: var(--primary-color, #1976d2);
          color: white;
          border-color: var(--primary-color, #1976d2);
        }

        .mg-btn-primary:hover {
          background: #1565c0;
        }

        .mg-btn-danger {
          background: var(--error-color, #d32f2f);
          color: white;
          border-color: var(--error-color, #d32f2f);
        }

        .mg-btn-outline {
          background: transparent;
        }

        .mg-btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
        }

        .mg-table-container {
          overflow-x: auto;
        }

        .mg-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .mg-table th, .mg-table td {
          padding: 0.5rem 0.75rem;
          text-align: start;
          border-bottom: 1px solid var(--border-color, #e0e0e0);
        }

        .mg-table thead {
          background: var(--background-color, #f5f5f5);
        }

        .mg-th-sortable {
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }

        .mg-th-sortable:hover {
          background: var(--hover-color, #eeeeee);
        }

        .mg-row-editing {
          background: #fff8e1;
        }

        .mg-cell-input {
          width: 100%;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--primary-color, #1976d2);
          border-radius: 4px;
          font-size: 0.875rem;
          outline: none;
        }

        .mg-actions-cell {
          white-space: nowrap;
          display: flex;
          gap: 0.25rem;
        }

        .mg-empty {
          text-align: center;
          padding: 2rem !important;
          color: var(--text-secondary, #757575);
        }

        .mg-pagination {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          border-top: 1px solid var(--border-color, #e0e0e0);
          font-size: 0.875rem;
        }

        .mg-row-count {
          color: var(--text-secondary, #757575);
          margin-inline-start: auto;
        }

        .mg-field-toggles {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: var(--background-color, #f5f5f5);
          border-bottom: 1px solid var(--border-color, #e0e0e0);
          align-items: center;
        }

        .mg-add-form {
          padding: 1rem 1.5rem;
          background: #f0f7ff;
          border-bottom: 1px solid var(--border-color, #e0e0e0);
        }

        .mg-add-fields {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .mg-add-field {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          min-width: 150px;
        }

        .mg-add-field label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-secondary, #757575);
        }

        .mg-add-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.75rem;
        }

        .mg-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .mg-modal {
          background: var(--surface-color, #fff);
          border-radius: var(--radius-lg, 12px);
          padding: 1.5rem;
          max-width: 90vw;
          max-height: 80vh;
          overflow: auto;
          min-width: 400px;
        }

        .mg-import-preview {
          margin: 1rem 0;
          overflow-x: auto;
        }

        .mg-preview-note {
          font-size: 0.75rem;
          color: var(--text-secondary, #757575);
          margin-top: 0.5rem;
        }

        .mg-modal-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
        }

        /* ==================== MOBILE ==================== */
        @media (max-width: 768px) {
          .magic-grid {
            border-radius: var(--radius-md, 8px);
          }

          .mg-title {
            padding: 0.75rem 0.75rem 0;
            font-size: 1rem;
          }

          .mg-toolbar {
            padding: 0.75rem;
            gap: 0.5rem;
          }

          .mg-search-wrap {
            flex: 1;
            min-width: 0;
          }

          .mg-search {
            min-width: 0;
            width: 100%;
          }

          .mg-toolbar-right {
            margin-inline-start: 0;
            width: 100%;
            flex-wrap: wrap;
          }

          .mg-table-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .mg-table {
            font-size: 0.8125rem;
            min-width: 500px;
          }

          .mg-table th, .mg-table td {
            padding: 0.375rem 0.5rem;
          }

          .mg-btn {
            min-height: 44px;
            padding: 0.5rem 0.75rem;
          }

          .mg-btn-sm {
            min-height: 36px;
            padding: 0.375rem 0.5rem;
          }

          .mg-add-form {
            padding: 0.75rem;
          }

          .mg-add-fields {
            flex-direction: column;
          }

          .mg-add-field {
            min-width: 0;
            width: 100%;
          }

          .mg-cell-input {
            min-height: 44px;
            padding: 0.5rem;
          }

          .mg-pagination {
            padding: 0.5rem 0.75rem;
            flex-wrap: wrap;
            gap: 0.5rem;
            font-size: 0.8125rem;
          }

          .mg-field-toggles {
            padding: 0.5rem 0.75rem;
          }

          .mg-modal {
            min-width: auto;
            width: 95vw;
            max-height: 90vh;
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
}
